import { FormEvent, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import type { AppRole } from '../types';
import { roleHome } from '../lib/roleNavigation';
import { isSupabaseConfigured, supabase } from '../lib/supabase';
import { useAuth } from '../state/AuthContext';

export function LoginPage() {
  const { user, signIn, signInDemo, isSupabaseConfigured, isDemoModeEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');

  if (user) return <Navigate to={roleHome[user.role]} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage('');
    try {
      await signIn(email, password);
      const destination = (location.state as { from?: string } | null)?.from;
      navigate(destination || '/app', { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to sign in.');
    }
  }

  async function demo(role: AppRole) {
    setMessage('');
    try {
      await signInDemo(role);
      navigate(roleHome[role]);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to start demo mode.');
    }
  }

  return (
    <main className={`auth-page ${isDemoModeEnabled ? '' : 'single'}`}>
      <section className="auth-card">
        <img className="auth-piko" src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
        <p className="eyebrow">Welcome back</p>
        <h1>Log in to QuizPiko</h1>
        <p>{isSupabaseConfigured ? 'Use your registered account.' : 'Supabase is not connected yet. Use a demo portal below.'}</p>
        <form onSubmit={submit}>
          <label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={8} /></label>
          {message && <div className="form-message error">{message}</div>}
          <button className="button" type="submit" disabled={!isSupabaseConfigured}>Log in securely</button>
        </form>
        {isSupabaseConfigured && <p className="auth-link"><Link to="/forgot-password">Forgot your password?</Link></p>}
        <p className="auth-link">New to QuizPiko? <Link to="/register">Create an account</Link></p>
      </section>
      {isDemoModeEnabled && (
        <section className="demo-panel">
          <p className="eyebrow">Instant preview</p>
          <h2>Open a demo portal</h2>
          <p>Demo sessions use local browser data and do not change Supabase.</p>
          <div className="demo-buttons">
            {(['super_admin', 'content_admin', 'reviewer', 'teacher', 'parent', 'student'] as AppRole[]).map((role) => (
              <button key={role} type="button" onClick={() => void demo(role)}>{role.replaceAll('_', ' ')}</button>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    setIsError(false);

    try {
      if (!isSupabaseConfigured) throw new Error('Supabase staging is not connected.');
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      if (error) throw error;
      setMessage('If this email belongs to a QuizPiko account, a secure recovery link has been sent.');
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof Error ? error.message : 'Unable to request a recovery link.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page single">
      <section className="auth-card wide">
        <img className="auth-piko" src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
        <p className="eyebrow">Secure account recovery</p>
        <h1>Reset your QuizPiko password</h1>
        <p>Enter your registered email. We will send a time-limited recovery link without changing your role or account.</p>
        <form onSubmit={submit}>
          <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          {message && <div className={`form-message ${isError ? 'error' : ''}`} role="status">{message}</div>}
          <button className="button" type="submit" disabled={busy || !isSupabaseConfigured}>{busy ? 'Sending securely…' : 'Send recovery link'}</button>
        </form>
        <p className="auth-link"><Link to="/login">Back to login</Link></p>
      </section>
    </main>
  );
}

export function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [sessionReady, setSessionReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSessionReady(Boolean(data.session));
      setChecking(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === 'PASSWORD_RECOVERY' || session) setSessionReady(Boolean(session));
      setChecking(false);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage('');

    if (password.length < 10) {
      setMessage('Use at least 10 characters.');
      return;
    }
    if (password !== confirmation) {
      setMessage('The two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      await supabase.auth.signOut({ scope: 'global' });
      setComplete(true);
      setPassword('');
      setConfirmation('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to update the password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page single">
      <section className="auth-card wide">
        <img className="auth-piko" src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
        <p className="eyebrow">Protected password update</p>
        <h1>{complete ? 'Password updated' : 'Choose a new password'}</h1>
        {complete ? (
          <>
            <div className="form-message" role="status">Your password was changed and all existing QuizPiko sessions were signed out.</div>
            <p className="auth-link"><Link className="button" to="/login">Log in with the new password</Link></p>
          </>
        ) : checking ? (
          <p>Checking the secure recovery link…</p>
        ) : !sessionReady ? (
          <>
            <div className="form-message error" role="alert">This recovery link is missing, invalid or expired.</div>
            <p className="auth-link"><Link to="/forgot-password">Request a new recovery link</Link></p>
          </>
        ) : (
          <form onSubmit={submit}>
            <label>New password<input type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={10} /></label>
            <label>Confirm new password<input type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required minLength={10} /></label>
            {message && <div className="form-message error" role="alert">{message}</div>}
            <button className="button" type="submit" disabled={busy}>{busy ? 'Updating securely…' : 'Set new password'}</button>
          </form>
        )}
      </section>
    </main>
  );
}

export function RegisterPage() {
  const { signUp, isSupabaseConfigured } = useAuth();
  const [accountType, setAccountType] = useState<'parent' | 'teacher'>('parent');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage('');
    try {
      await signUp(displayName, email, password, accountType);
      setMessage('Account submitted. Check your email if confirmation is enabled.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to register.');
    }
  }

  return (
    <main className="auth-page single">
      <section className="auth-card wide">
        <p className="eyebrow">Join QuizPiko</p>
        <h1>Create a parent or teacher account</h1>
        <div className="segmented">
          <button type="button" className={accountType === 'parent' ? 'selected' : ''} onClick={() => setAccountType('parent')}>Parent</button>
          <button type="button" className={accountType === 'teacher' ? 'selected' : ''} onClick={() => setAccountType('teacher')}>Teacher</button>
        </div>
        <form onSubmit={submit}>
          <label>Full name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></label>
          <label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={8} /></label>
          {message && <div className="form-message">{message}</div>}
          <button className="button" type="submit" disabled={!isSupabaseConfigured}>Create {accountType} account</button>
        </form>
        {!isSupabaseConfigured && <p className="notice">Connect Supabase in <code>.env</code> to activate registration.</p>}
      </section>
    </main>
  );
}
